/* eslint-disable require-unicode-regexp */
import bowser from 'bowser';
import _ from 'lodash';
import { Browsers, OS } from '../../../modules/core/constants/env';
import { CHROME_VERSION_WITH_BROKEN_CANVAS, IOS_VERSIONS_BUGGED_FIREWALL, IOS_VERSIONS_LOW_AUDIO, IOS_VERSIONS_UNSUPPORTED_H264, SAFARI_VERSIONS_BUGGED_FIREWALL, SAFARI_VERSIONS_ECHO, } from '../../../modules/call/constants';
import { SystemDeviceInfo } from '../system-device-info/system-device-info';
const deviceInfo = new SystemDeviceInfo();
export default class Browser {
    isSupported() {
        if (this.isFacebookApp()) {
            return false;
        }
        const isUnsupportedIosBrowser = this.isLegacyMobileSafari() ||
            this.isIosInAppBrowser() ||
            this.isUnsupportedIosBrowser();
        const isSomethingSupported = Object.keys(process.env.SUPPORTED_BROWSERS).some((name) => bowser[name] === true);
        return !isUnsupportedIosBrowser && isSomethingSupported;
    }
    isHandheld() {
        return bowser.tablet || bowser.mobile || this.isIPad();
    }
    get isDesktop() {
        return !this.isHandheld();
    }
    isEdge() {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const hasEdgeBrand = navigator?.userAgentData?.brands?.some((brandVersion) => brandVersion.brand.includes('Edge'));
        if (hasEdgeBrand) {
            return true;
        }
        return /\sedg\//i.test(navigator.userAgent) || Boolean(bowser.msedge);
    }
    isIPad() {
        return (navigator.userAgent.includes('iPad') ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
    }
    isMacOs() {
        return bowser.mac;
    }
    isPrerender() {
        return Boolean(/(prerender)/i.test(window.navigator.userAgent));
    }
    // If it's ios safari and still lower than 11 - probably they need the app
    isLegacyMobileSafari() {
        return (bowser.ios &&
            bowser.safari &&
            Number.parseInt(bowser.version) < 11);
    }
    isFacebookApp() {
        const ua = 
        // @ts-expect-error TODO: add opera to window types
        navigator.userAgent || navigator.vendor || window.opera;
        return ua.includes('FBAN') || ua.includes('FBAV');
    }
    supportsMediaDevices() {
        return Boolean(window.navigator.mediaDevices);
    }
    supportsCaptureHandleConfig() {
        return (this.isDesktop &&
            Boolean(window.navigator.mediaDevices.setCaptureHandleConfig));
    }
    supportsOnDeviceChange() {
        if (!this.supportsMediaDevices()) {
            return false;
        }
        return typeof window.navigator.mediaDevices.addEventListener === 'function';
    }
    isSafari(version) {
        if (Boolean(version)) {
            return (Boolean(bowser.safari) &&
                String(bowser.version).startsWith(String(version)));
        }
        return Boolean(bowser.safari);
    }
    isIosSupportedH264() {
        return (this.isIos() &&
            !IOS_VERSIONS_UNSUPPORTED_H264.includes(String(bowser.version)));
    }
    /**
     * @description https://doxyme.atlassian.net/browse/CS-3540
     */
    isIosWithLowAudio() {
        return (this.isIos() && IOS_VERSIONS_LOW_AUDIO.includes(String(bowser.version)));
    }
    /**
     * @description https://doxyme.atlassian.net/browse/CS-4563
     */
    isIosWithBuggedFirewall() {
        return (this.isIos() &&
            IOS_VERSIONS_BUGGED_FIREWALL.some((version) => String(bowser.version).startsWith(String(version))));
    }
    /**
     * @description https://doxyme.atlassian.net/browse/CS-5030
     */
    isSafariWithBuggedFirewall() {
        return SAFARI_VERSIONS_BUGGED_FIREWALL.some((version) => this.isSafari(version));
    }
    /**
     * @description https://doxyme.atlassian.net/browse/CS-5030
     */
    isWithBuggedFirewall() {
        return (this.isIosWithBuggedFirewall() ||
            this.isSafariWithBuggedFirewall() ||
            this.isFirefox());
    }
    /**
     * @description https://doxyme.atlassian.net/browse/CS-4580
     */
    isMobileSafariWithEcho() {
        return (this.isMobileSafari() &&
            SAFARI_VERSIONS_ECHO.some((version) => this.isSafari(version)));
    }
    isFirefox(version) {
        if (Boolean(version)) {
            return (Boolean(bowser.firefox) &&
                String(bowser.version).startsWith(String(version)));
        }
        return Boolean(bowser.firefox);
    }
    isChrome(version) {
        if (version != null) {
            return (Boolean(bowser.chrome) &&
                String(bowser.version).startsWith(String(version)));
        }
        return Boolean(bowser.chrome);
    }
    isWithBrokenCanvas() {
        const isChromeWithBrokenCanvas = this.isChrome() &&
            Number(bowser.version) >= CHROME_VERSION_WITH_BROKEN_CANVAS;
        return isChromeWithBrokenCanvas || this.isFirefox();
    }
    isMobileSafari() {
        return bowser.ios && bowser.safari;
    }
    isDesktopSafari() {
        return !bowser.ios && bowser.safari;
    }
    // There is a specific issue with Chrome > 86 on android devices
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1139900
    isAndroidChrome86() {
        return bowser.android && bowser.chrome && bowser.version >= 86;
    }
    isIos() {
        return this.isMobileSafari() || this.isIPad() || this.isIosButNotSafari();
    }
    supportsOnlySDQuality() {
        // Only SD works on old iOS
        return this.isMobileSafari() && bowser.version < 13;
    }
    isIosInAppBrowser() {
        if (!this.isIosButNotSafari()) {
            return false;
        }
        // Hot fix, for now treat everything that is not chrome/firefox as an in-app browser
        // Sometimes facebook app can be detected as chrome
        const isInApp = (!bowser.chrome && !bowser.firefox) || this.isFacebookApp();
        return isInApp;
    }
    isIosButNotSafari() {
        // Built in facebook browser on ios can be detected as safari
        // check for mediaDevice support as other in app browsers also look like Safari
        return (bowser.ios &&
            (!bowser.safari || this.isFacebookApp() || !this.supportsMediaDevices()));
    }
    isNarrowerThan(maxWidthPx = 601) {
        return window.innerWidth < maxWidthPx;
    }
    supportsScreensharing() {
        return Boolean(bowser.chrome || bowser.firefox || (bowser.safari && bowser.version >= 13));
    }
    isInIframe() {
        try {
            return window.self !== window.top;
        }
        catch {
            return true;
        }
    }
    getHelpArticleUrl() {
        const { browser, tablet, os, osversion } = this.getSystemInfo();
        if (browser === Browsers.Edge) {
            return 'https://help.doxy.me/en/articles/3867526-camera-mic-permission-edge';
        }
        if (browser === Browsers.Chrome) {
            return 'https://help.doxy.me/en/articles/2427135-camera-mic-permission-chrome';
        }
        if (browser === Browsers.Safari) {
            return 'https://help.doxy.me/en/articles/836274-camera-mic-permission-safari';
        }
        if (browser === Browsers.Firefox) {
            return 'https://help.doxy.me/en/articles/2427185-camera-mic-permission-firefox';
        }
        if (os === OS.MacOS) {
            return 'https://help.doxy.me/en/articles/3727780-camera-mic-permission-macos';
        }
        if (os === OS.Windows && tablet) {
            return 'https://help.doxy.me/en/articles/2427143-camera-mic-permission-surface-pro';
        }
        if (os === OS.Windows && String(osversion).startsWith('10')) {
            return 'https://help.doxy.me/en/articles/3644635-camera-mic-permission-windows-10';
        }
        return 'https://help.doxy.me/en/articles/95877-give-permission-to-use-your-webcam';
    }
    isUpToDate() {
        if (navigator.webdriver) {
            return true;
        }
        if ((bowser.ios || bowser.android) && (bowser.tablet || bowser.mobile)) {
            return true;
        }
        const browsers = process.env.SUPPORTED_BROWSERS;
        const minVersion = _.find(browsers, (v, name) => bowser[name]);
        if (!minVersion) {
            return true;
        }
        return (Number.parseInt(bowser.version) >= Number.parseInt(minVersion));
    }
    isMobileScreen() {
        return /*this.isHandheld() && */ this.isNarrowerThan(1024);
    }
    get isMobileWindowWidth() {
        return this.isNarrowerThan(801);
    }
    getOSVersion() {
        return Number.parseFloat(bowser.osversion);
    }
    isUnsupportedIosBrowser() {
        if (!bowser.ios) {
            return false;
        }
        if (bowser.safari) {
            return false;
        }
        // starting from iOS 14.3 all browsers supported
        return bowser.osversion < 14.3;
    }
    get platform() {
        return bowser.mobile || bowser.tablet ? 'mobile' : 'desktop';
    }
    get isMobile() {
        return this.platform === 'mobile';
    }
    getSystemInfo() {
        const browser = this.isEdge()
            ? Browsers.Edge
            : Object.values(Browsers).find((name) => bowser[name]);
        const os = Object.values(OS).find((i) => bowser[i]);
        return {
            browser,
            os,
            platform: this.platform,
            tablet: Boolean(bowser.tablet),
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            browserVersion: Number.parseInt(bowser.version) || null,
            supportsScreensharing: this.supportsScreensharing(),
            osversion: bowser.osversion,
            browserLanguage: navigator.language || navigator.userLanguage,
            referrer: document.referrer,
        };
    }
    // for tracking purposes
    getPlatformData() {
        const systemInfo = this.getSystemInfo();
        return {
            os: systemInfo.os,
            os_version: systemInfo.osversion,
            browser: systemInfo.browser,
            browser_version: systemInfo.browserVersion,
            platform: systemInfo.tablet ? 'tablet' : systemInfo.platform,
            device_model: deviceInfo.model,
        };
    }
    shouldEnableManifest() {
        // for now disable manifest/pwa for ios completely
        return !bowser.ios;
    }
    tooltipTrigger() {
        return this.isHandheld() ? 'manual' : 'hover focus';
    }
}
export const browser = new Browser();
